import React, { useState, useEffect } from 'react';
import { FiUser, FiSave, FiLock, FiAward } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import userService from '../../services/user.service';
import authService from '../../services/auth.service';
import AddressBook from '../../components/address/AddressBook';

const ProfilePage = () => {
  const { t } = useLanguage();
  const { user, loadUser } = useAuth();
  const [loyaltyData, setLoyaltyData] = useState(null);

  const [profileForm, setProfileForm] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    avatar: user?.avatar || '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    const fetchLoyalty = async () => {
      try {
        const { data } = await userService.getLoyaltyPoints();
        setLoyaltyData(data.data);
      } catch {}
    };
    fetchLoyalty();
  }, []);

  const handleProfileChange = (e) => {
    setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await userService.updateProfile(profileForm);
      toast.success('Profile updated!');
      await loadUser(); // Refresh user data in context
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setSavingPassword(true);
    try {
      await authService.updatePassword({
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success('Password updated!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="section-padding">
      <div className="container fd-page-container" style={{ maxWidth: '700px' }}>
        <h2 className="mb-4"><FiUser className="me-2" />{t("myProfile")}</h2>

        {/* Profile Form */}
        <div className="dashboard-card mb-4">
          <h5 className="mb-3">Personal Information</h5>
          <form onSubmit={handleProfileSubmit}>
            <div className="row mb-3">
              <div className="col-md-6">
                <label className="form-label">First Name</label>
                <input
                  type="text" name="firstName" className="form-control"
                  value={profileForm.firstName} onChange={handleProfileChange} required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Last Name</label>
                <input
                  type="text" name="lastName" className="form-control"
                  value={profileForm.lastName} onChange={handleProfileChange} required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label">Email</label>
              <input type="email" className="form-control" value={user?.email || ''} disabled />
              <small className="text-muted">Email cannot be changed</small>
            </div>

            <div className="mb-3">
              <label className="form-label">Phone</label>
              <input
                type="tel" name="phone" className="form-control"
                placeholder="+1 (555) 000-0000"
                value={profileForm.phone} onChange={handleProfileChange}
              />
            </div>

            <div className="mb-3">
              <label className="form-label">Avatar URL</label>
              <input
                type="url" name="avatar" className="form-control"
                placeholder="https://..."
                value={profileForm.avatar} onChange={handleProfileChange}
              />
              {profileForm.avatar && (
                <img src={profileForm.avatar} alt="Avatar" className="mt-2 rounded-circle border"
                  style={{ width: '48px', height: '48px', objectFit: 'cover' }} />
              )}
            </div>

            <button type="submit" className="btn btn-fd-primary" disabled={savingProfile}>
              <FiSave className="me-1" /> {savingProfile ? 'Saving...' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Password Form */}
        <div className="dashboard-card">
          <h5 className="mb-3"><FiLock className="me-2" />Change Password</h5>
          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-3">
              <label className="form-label">Current Password</label>
              <input
                type="password" name="currentPassword" className="form-control"
                value={passwordForm.currentPassword} onChange={handlePasswordChange} required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">New Password</label>
              <input
                type="password" name="newPassword" className="form-control"
                value={passwordForm.newPassword} onChange={handlePasswordChange}
                required minLength={6}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password" name="confirmPassword" className="form-control"
                value={passwordForm.confirmPassword} onChange={handlePasswordChange}
                required minLength={6}
              />
            </div>
            <button type="submit" className="btn btn-fd-outline" disabled={savingPassword}>
              <FiLock className="me-1" /> {savingPassword ? 'Updating...' : 'Update Password'}
            </button>
          </form>
        </div>
      </div>

      {/* Loyalty Points */}
      {loyaltyData && (
        <div className="col-md-6">
          <div className="dashboard-card">
            <h5 className="mb-3"><FiAward className="me-2" style={{ color: '#EAB308' }} />Loyalty Points</h5>
            <div className="row g-3">
              <div className="col-6">
                <div className="p-3 rounded text-center" style={{ background: '#FEF3C7' }}>
                  <div className="fw-bold fs-3" style={{ color: '#B45309' }}>{loyaltyData.balance}</div>
                  <small className="text-muted">Available Points</small>
                </div>
              </div>
              <div className="col-6">
                <div className="p-3 rounded text-center bg-light">
                  <div className="fw-bold fs-3">{loyaltyData.lifetime}</div>
                  <small className="text-muted">Lifetime Earned</small>
                </div>
              </div>
            </div>
            <div className="mt-3 p-2 rounded small" style={{ background: '#F9FAFB' }}>
              <p className="mb-1"><strong>Redeemable value:</strong> ${loyaltyData.redeemableValue?.toFixed(2)}</p>
              <p className="mb-0 text-muted">{loyaltyData.conversionRate}</p>
            </div>
            <p className="text-muted small mt-2 mb-0">
              Earn 1 point for every $1 spent. Redeem points at checkout for discounts!
            </p>
          </div>
        </div>
      )}

      {/* Address Book */}
      <div className="dashboard-card mt-4">
        <AddressBook mode="manage" />
      </div>
    </div>
  );
};

export default ProfilePage;
